"use strict";
/**
 * D1 JSON Parsing Utilities
 *
 * Handles the quirks of D1's JSON-as-TEXT storage:
 * - Literal "null" strings
 * - Empty strings
 * - Safely parsing with error handling
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseD1Json = parseD1Json;
exports.fromPrismaToD1Row = fromPrismaToD1Row;
exports.parseD1GameState = parseD1GameState;
exports.serializeForD1 = serializeForD1;
exports.clearD1Cache = clearD1Cache;
var tslib_1 = require("tslib");
var Game = tslib_1.__importStar(require("../game"));
// Simple request-scoped cache for parsed values
var parseCache = new Map();
/**
 * Generic D1 JSON parser that handles all the quirks
 * @param value - The string value from D1 (or already parsed value)
 * @param defaultValue - Default value if parsing fails or value is null
 * @returns Parsed value or default
 */
function parseD1Json(value, defaultValue) {
    // Handle null, undefined, or literal "null" string
    if (value === null || value === undefined || value === "null" || value === "") {
        return defaultValue;
    }
    // If already parsed, return as-is
    if (typeof value !== "string") {
        return value;
    }
    try {
        return JSON.parse(value);
    }
    catch (e) {
        console.error("[parseD1Json] Failed to parse:", value, e);
        return defaultValue;
    }
}
/**
 * Prisma's types are wrong for D1. D1 returns JSON columns as strings,
 * not parsed objects. This is a fact. We're not going to pretend otherwise.
 *
 * This function exists solely to tell TypeScript the truth about what D1
 * actually returns at runtime.
 *
 * @param row - The row from Prisma (incorrectly typed with JsonValue fields)
 * @returns The same row typed correctly for D1 (string fields)
 */
function fromPrismaToD1Row(row) {
    // Yes, we're using 'any'. Because Prisma is lying to us anyway.
    // At least we're being honest about the lie.
    return row;
}
/**
 * Parse D1 game state row into application format with caching
 * @param row - Raw game state from D1
 * @param options - Cache options
 * @returns Parsed game state
 */
function parseD1GameState(row, options) {
    if (options === void 0) { options = { maxSize: 100 }; }
    // Defensive null check (matching toGameStateObj behavior)
    if (!row) {
        console.error("[parseD1GameState] Received null or undefined row");
        throw new Error("parseD1GameState received invalid input");
    }
    // Check cache first
    var cacheKey = "".concat(row.gameId, "-").concat(row.sequence);
    if (parseCache.has(cacheKey)) {
        return parseCache.get(cacheKey);
    }
    // Parse dice with special handling for D1's "null" string
    // Default to empty array for compatibility (matching toGameStateObj behavior)
    var dice = [];
    if (typeof row.dice === "string") {
        if (row.dice === "null") {
            dice = []; // Changed from null to [] for compatibility
        }
        else {
            try {
                var parsed_1 = JSON.parse(row.dice);
                dice = Array.isArray(parsed_1) ? parsed_1 : [];
            }
            catch (_a) {
                dice = [];
            }
        }
    }
    else {
        dice = row.dice || [];
    }
    // Parse board with fallback to initial board
    var board;
    try {
        board = parseD1Json(row.board, Game.Board.initStartingBoard());
        if (!Array.isArray(board)) {
            board = Game.Board.initStartingBoard();
        }
    }
    catch (_b) {
        board = Game.Board.initStartingBoard();
    }
    // Parse bar and off with validation
    var bar = parseD1Json(row.bar, { white: 0, black: 0 });
    var off = parseD1Json(row.off, { white: 0, black: 0 });
    // Validate bar and off have correct structure
    if (typeof bar.white !== "number" || typeof bar.black !== "number") {
        bar.white = 0;
        bar.black = 0;
    }
    if (typeof off.white !== "number" || typeof off.black !== "number") {
        off.white = 0;
        off.black = 0;
    }
    // Parse moves
    var moves = parseD1Json(row.moves, []);
    // Parse dates
    var createdAt = row.createdAt instanceof Date ? row.createdAt : new Date(row.createdAt);
    var turnStartedAt = row.turnStartedAt
        ? row.turnStartedAt instanceof Date
            ? row.turnStartedAt
            : new Date(row.turnStartedAt)
        : null;
    // Validate sequence is a number (matching toGameStateObj behavior)
    var sequence = typeof row.sequence === "number" ? row.sequence : 0;
    // Handle nullable fields - default to empty strings for compatibility
    var currentPlayerId = row.currentPlayerId || "";
    var currentPlayerColor = row.currentPlayerColor || "white";
    var parsed = {
        // GameState fields
        gameId: row.gameId,
        board: board,
        bar: bar,
        off: off,
        currentPlayerColor: currentPlayerColor,
        currentPlayerId: currentPlayerId,
        dice: dice,
        sequence: sequence,
        moves: moves,
        // Additional ParsedGameState fields
        id: row.id,
        createdAt: createdAt,
        turnStartedAt: turnStartedAt
    };
    // Cache the result
    parseCache.set(cacheKey, parsed);
    // Clear cache if it gets too large
    if (parseCache.size > (options.maxSize || 100)) {
        parseCache.clear();
    }
    return parsed;
}
/**
 * Convert application game state to D1 format (for saving)
 * @param state - Application game state
 * @returns Object ready for D1 storage
 */
function serializeForD1(state) {
    return {
        id: state.id,
        gameId: state.gameId,
        board: JSON.stringify(state.board),
        bar: JSON.stringify(state.bar),
        off: JSON.stringify(state.off),
        dice: state.dice ? JSON.stringify(state.dice) : null,
        moves: JSON.stringify(state.moves || []),
        currentPlayerId: state.currentPlayerId,
        currentPlayerColor: state.currentPlayerColor,
        sequence: state.sequence,
        createdAt: state.createdAt,
        turnStartedAt: state.turnStartedAt
    };
}
/**
 * Clear the parse cache (useful for testing or memory management)
 */
function clearD1Cache() {
    parseCache.clear();
}
//# sourceMappingURL=parsers.js.map